import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import { ArrowUpFromLine, Braces, Check, Copy, RadioTower, Scale, Search, Send, Settings2, ShieldCheck, SlidersHorizontal, UserPlus, X, Zap } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getSettings, putSetting, type Setting } from '../api/admin'
import { PageHeader } from '../components/layout'
import {
  Badge,
  Button,
  Card,
  Chip,
  EmptyState,
  Field,
  IconButton,
  Input,
  Modal,
  SegmentedControl,
  Skeleton,
  SkeletonText,
  Switch,
  Textarea,
  cx,
  describeError,
  prefersReducedMotion,
  useToast,
  useTotp,
  when,
} from '../components/ui/Ui'
import { parseSignalCodeTtl } from '../lib/settings'
import styles from './Settings.module.css'

/** The access kinds the bot may post to the channel (`telegram.channel_access_kinds`). */
const CHANNEL_ACCESS_KINDS: readonly string[] = ['public', 'base', 'premium', 'bonus', 'agent']

/** The same words the signal screen uses for an access kind. */
const ACCESS_RU: Record<string, string> = { public: 'публичный', base: 'базовый', premium: 'премиум', bonus: 'бонусный', agent: 'агентский' }

/**
 * The value keeps the type it already has: a text setting stays text even when
 * it looks like a number (a Telegram channel id `-100…` must not become one),
 * a list or an object is read as JSON of the same shape. A setting with no
 * value yet falls back to guessing: true / false, a number, JSON, or text.
 * `telegram.channel_access_kinds` must be a list of known kinds whatever it
 * holds now: anything else silently stops channel posts. The same goes for
 * `signals.code_ttl_minutes`: a typo would quietly bring the default back to 5 minutes.
 */
function parseValue(raw: string, current: unknown, key: string): unknown {
  const text = raw.trim()
  if (key === 'signals.code_ttl_minutes') {
    if (parseSignalCodeTtl(text) === null) throw new Error('Целое число минут от 1 до 1440, например 5.')
    return typeof current === 'number' ? Number(text) : text
  }
  if (key === 'telegram.channel_access_kinds') {
    let kinds: unknown
    try {
      kinds = JSON.parse(text) as unknown
    } catch {
      throw new Error('Это не JSON. Список пишется так: ["public"].')
    }
    if (!Array.isArray(kinds)) throw new Error('Нужен список, например ["public"].')
    if (!kinds.every((kind) => typeof kind === 'string' && CHANNEL_ACCESS_KINDS.includes(kind))) throw new Error('Допустимо: public, base, premium, bonus, agent.')
    return kinds
  }
  if (typeof current === 'string') return text
  if (typeof current === 'boolean') {
    if (text === 'true') return true
    if (text === 'false') return false
    throw new Error('Здесь только true или false.')
  }
  if (typeof current === 'number') {
    if (text !== '' && Number.isFinite(Number(text))) return Number(text)
    throw new Error('Здесь нужно число.')
  }
  if (Array.isArray(current) || (typeof current === 'object' && current !== null)) {
    let parsed: unknown
    try {
      parsed = JSON.parse(text) as unknown
    } catch {
      throw new Error(Array.isArray(current) ? 'Это не JSON. Список пишется так: ["public"].' : 'Это не JSON. Объект пишется так: {"ключ": 1}.')
    }
    if (Array.isArray(current) && !Array.isArray(parsed)) throw new Error('Нужен список, например ["public"].')
    if (!Array.isArray(current) && (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))) throw new Error('Нужен объект JSON, например {"ключ": 1}.')
    return parsed
  }
  if (text === 'true') return true
  if (text === 'false') return false
  if (/^-?\d+(\.\d+)?$/.test(text) && text.length < 16 && !text.startsWith('-100')) return Number(text)
  if (text.startsWith('[') || text.startsWith('{')) {
    try {
      return JSON.parse(text) as unknown
    } catch {
      return text
    }
  }
  return text
}

function editValue(value: unknown): string {
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) return JSON.stringify(value)
  return String(value ?? '')
}

function valueHint(current: unknown): string {
  if (typeof current === 'string') return 'Сохраняется как текст, даже если похоже на число.'
  if (typeof current === 'boolean') return 'true или false.'
  if (typeof current === 'number') return 'Число.'
  if (Array.isArray(current)) return 'JSON-список, например ["public", "base"].'
  if (typeof current === 'object' && current !== null) return 'JSON-объект, например {"ключ": 1}.'
  return 'true / false и числа сохраняются типизированно, [..] и {..} — как JSON, остальное — как текст.'
}

/* ------------------------------------------------------------------------ */
/* what each key is, in words                                               */
/* ------------------------------------------------------------------------ */

type Control = 'bool' | 'number' | 'text' | 'kinds' | 'json'

interface KeyMeta {
  title: string
  /** Shown inside the field's right edge. */
  unit?: string
  placeholder?: string
}

/** Human names and units for the known keys; the order here is the order on the page. */
const KEYS: Record<string, KeyMeta> = {
  'auth.invitation_required': { title: 'Регистрация только по коду' },
  'kyc.required_to_use_funds': { title: 'Операции только после верификации' },

  'withdrawals.review_required': { title: 'Проверка выводов оператором' },
  'withdrawals.daily_limit_usdt': { title: 'Суточный лимит вывода', unit: 'USDT' },
  'withdrawals.daily_limit_unverified_usdt': { title: 'Суточный лимит без верификации', unit: 'USDT' },
  'withdrawals.four_eyes_threshold_usdt': { title: 'Порог двух подписей', unit: 'USDT' },
  'withdrawals.address_book_required': { title: 'Только адреса из адресной книги' },
  'withdrawals.dynamic_fee': { title: 'Комиссия по цене газа' },
  'withdrawals.dynamic_fee_multiplier': { title: 'Запас к оценке газа' },
  'withdrawals.tron_fee_limit_trx': { title: 'Предел комиссии Tron', unit: 'TRX' },
  'withdrawals.solana_priority_fee_microlamports': { title: 'Приоритетная комиссия Solana', unit: 'µlamports' },

  'withdrawals.auto.enabled': { title: 'Автовывод' },
  'withdrawals.auto.max_per_withdrawal_usdt': { title: 'Наибольшая сумма заявки', unit: 'USDT' },
  'withdrawals.auto.daily_cap_usdt': { title: 'Бюджет платформы на сутки', unit: 'USDT' },
  'withdrawals.auto.per_user_daily_usdt': { title: 'Сумма на пользователя в сутки', unit: 'USDT' },
  'withdrawals.auto.per_user_daily_count': { title: 'Заявок на пользователя в сутки', unit: 'шт.' },
  'withdrawals.auto.min_account_age_hours': { title: 'Возраст аккаунта не меньше', unit: 'ч' },
  'withdrawals.auto.security_cooldown_hours': { title: 'Пауза после смены защиты', unit: 'ч' },
  'withdrawals.auto.require_known_address': { title: 'Только знакомые адреса' },

  'signals.default_market': { title: 'Рынок по умолчанию' },
  'signals.code_ttl_minutes': { title: 'Срок кода по умолчанию', unit: 'мин' },
  'signals.display_timezone': { title: 'Часовой пояс сигналов', placeholder: 'Europe/Moscow' },

  'telegram.signals_channel': { title: 'Канал для кодов', placeholder: '@channel или -100…' },
  'telegram.signals_channel_url': { title: 'Ссылка на канал', placeholder: 'https://t.me/…' },
  'telegram.channel_access_kinds': { title: 'Какие сигналы уходят в канал' },

  'futures.enabled': { title: 'Приём фьючерсных ордеров' },
  'futures.leverage_tiers': { title: 'Плечо по объёму позиции' },
  'futures.funding_rate_8h': { title: 'Ставка финансирования за 8 ч', unit: 'доля' },

  'platform.support_email': { title: 'Почта поддержки', placeholder: 'support@…' },
  'platform.support_telegram': { title: 'Telegram поддержки', placeholder: '@username или https://t.me/…' },
  'platform.maintenance_message': { title: 'Баннер обслуживания', placeholder: 'Пусто — баннера нет' },
  'alerts.grace_minutes': { title: 'Задержка оповещений о сбоях', unit: 'мин' },

  'copy.enabled': { title: 'Копи-трейдинг' },
  'copy.max_allocation_usdt': { title: 'Наибольшая подписка', unit: 'USDT' },
  'copy.default_stop_loss_pct': { title: 'Стоп по умолчанию', unit: '%' },
}
const KEY_ORDER = Object.keys(KEYS)

interface Group {
  id: string
  title: string
  description: string
  icon: LucideIcon
  match: (key: string) => boolean
}

const GROUPS: readonly Group[] = [
  { id: 'registration', title: 'Регистрация', description: 'Кто может зарегистрироваться и что доступно до верификации.', icon: UserPlus, match: (key) => key.startsWith('auth.') || key.startsWith('kyc.') },
  { id: 'withdrawals', title: 'Выводы', description: 'Лимиты, проверка оператором и комиссии сетей.', icon: ArrowUpFromLine, match: (key) => key.startsWith('withdrawals.') && !key.startsWith('withdrawals.auto.') },
  { id: 'auto', title: 'Автовывод', description: 'Правила, по которым горячий кошелёк отправляет вывод без оператора.', icon: Zap, match: (key) => key.startsWith('withdrawals.auto.') },
  { id: 'signals', title: 'Сигналы', description: 'Форма нового сигнала и срок действия кода.', icon: RadioTower, match: (key) => key.startsWith('signals.') },
  { id: 'telegram', title: 'Telegram', description: 'Канал, куда бот публикует коды сигналов.', icon: Send, match: (key) => key.startsWith('telegram.') },
  { id: 'futures', title: 'Фьючерсы', description: 'Приём ордеров, плечо и финансирование.', icon: Scale, match: (key) => key.startsWith('futures.') },
  { id: 'platform', title: 'Платформа', description: 'Поддержка, обслуживание и оповещения операторов.', icon: Settings2, match: (key) => key.startsWith('platform.') || key.startsWith('alerts.') },
  { id: 'copy', title: 'Копи-трейдинг', description: 'Раздел выключен с 28 сентября; значения хранятся на случай возврата.', icon: Copy, match: (key) => key.startsWith('copy.') },
  { id: 'other', title: 'Прочее', description: 'Ключи без своего раздела.', icon: SlidersHorizontal, match: () => true },
]

function groupOf(key: string): Group {
  return GROUPS.find((group) => group.match(key)) ?? GROUPS[GROUPS.length - 1]
}

function titleOf(row: Setting): string {
  return KEYS[row.key]?.title ?? row.key
}

const NUMERIC = /^-?\d+(\.\d+)?$/

/** The control a value gets: the type it holds decides; a key with a unit is a figure even when it is kept as text. */
function controlOf(row: Setting): Control {
  if (row.key === 'telegram.channel_access_kinds') return 'kinds'
  const value = row.value
  if (typeof value === 'boolean') return 'bool'
  if (typeof value === 'number') return 'number'
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) return 'json'
  if (typeof value === 'string' && (KEYS[row.key]?.unit !== undefined || (NUMERIC.test(value) && !value.startsWith('-100')))) return 'number'
  return 'text'
}

/** The access kinds in their canonical order, so a reordered list is not a change. */
function kindsOf(raw: string): string[] | null {
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return null
    const known = CHANNEL_ACCESS_KINDS.filter((kind) => parsed.includes(kind))
    const unknown = parsed.filter((kind): kind is string => typeof kind === 'string' && !CHANNEL_ACCESS_KINDS.includes(kind))
    return [...known, ...unknown]
  } catch {
    return null
  }
}

function sameValue(row: Setting, raw: string): boolean {
  const control = controlOf(row)
  if (control === 'kinds') return JSON.stringify(kindsOf(raw)) === JSON.stringify(kindsOf(editValue(row.value)))
  if (control === 'json') {
    try {
      return JSON.stringify(JSON.parse(raw)) === JSON.stringify(row.value)
    } catch {
      return false
    }
  }
  return raw.trim() === editValue(row.value).trim()
}

type Filter = 'all' | 'changed' | 'default'

/* ------------------------------------------------------------------------ */
/* screen                                                                   */
/* ------------------------------------------------------------------------ */

export default function Settings(): JSX.Element {
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<string | null>(null)
  const [jsonEditing, setJsonEditing] = useState<Setting | null>(null)
  const [jsonText, setJsonText] = useState('')
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [flash, setFlash] = useState<string | null>(null)
  const [active, setActive] = useState<string>(GROUPS[0].id)
  const [search, setSearch] = useSearchParams()
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()
  const list = useQuery({ queryKey: ['settings'], queryFn: getSettings })
  const savedTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(savedTimer.current), [])

  const save = useMutation({
    mutationFn: ({ key, code, current, raw }: { key: string; code: string; current: unknown; raw: string }) => putSetting(key, parseValue(raw, current, key), code),
    onSuccess: (result) => {
      toast(`${result.key} сохранено`)
      // The saved row replaces the old one at once, so the control does not flick back before the refetch.
      queryClient.setQueryData<Setting[]>(['settings'], (current) => current?.map((row) => (row.key === result.key ? result : row)))
      setDrafts((current) => without(current, result.key))
      setJsonEditing((current) => (current?.key === result.key ? null : current))
      setSaved(result.key)
      window.clearTimeout(savedTimer.current)
      savedTimer.current = window.setTimeout(() => setSaved(null), 2600)
      void queryClient.invalidateQueries({ queryKey: ['settings'] })
      // The signal form and invites read the code lifetime, zone and invitation flag from there.
      void queryClient.invalidateQueries({ queryKey: ['platform-config'] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  const rows = useMemo(() => list.data ?? [], [list.data])

  const openJson = useCallback((row: Setting): void => {
    setJsonEditing(row)
    setJsonText(JSON.stringify(row.value ?? null, null, 2))
  }, [])

  // `/settings?edit=<key>` (e.g. from the channel card on «Сигналы») brings that key's editor up.
  const wanted = search.get('edit')
  useEffect(() => {
    if (wanted === null || list.data === undefined) return
    const row = list.data.find((item) => item.key === wanted)
    if (row) {
      setQuery('')
      setFilter('all')
      if (controlOf(row) === 'json') {
        openJson(row)
      } else {
        setFlash(row.key)
        window.setTimeout(() => {
          const element = document.getElementById(rowId(row.key))
          element?.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
          element?.querySelector<HTMLElement>('input, button[role="switch"], button[aria-pressed]')?.focus({ preventScroll: true })
        }, 60)
        window.setTimeout(() => setFlash((current) => (current === row.key ? null : current)), 2400)
      }
    }
    setSearch({}, { replace: true })
  }, [wanted, list.data, setSearch, openJson])

  const draftOf = (row: Setting): string => drafts[row.key] ?? editValue(row.value)
  const isDirty = (row: Setting): boolean => drafts[row.key] !== undefined && !sameValue(row, drafts[row.key])
  const setDraft = (row: Setting, raw: string): void => {
    setDrafts((current) => ({ ...current, [row.key]: raw }))
    setErrors((current) => without(current, row.key))
  }
  const reset = (row: Setting): void => {
    setDrafts((current) => without(current, row.key))
    setErrors((current) => without(current, row.key))
  }
  const commit = (row: Setting, raw: string): void => {
    try {
      parseValue(raw, row.value, row.key)
    } catch (error) {
      setErrors((current) => ({ ...current, [row.key]: error instanceof Error ? error.message : 'Значение не подходит' }))
      return
    }
    void withTotp((code) => save.mutateAsync({ key: row.key, code, current: row.value, raw }))
  }

  // Search and the changed/default filter; groups keep the order of the page.
  const needle = query.trim().toLowerCase()
  const visible = rows.filter((row) => {
    if (filter === 'changed' && row.isDefault) return false
    if (filter === 'default' && !row.isDefault) return false
    if (needle === '') return true
    return [row.key, titleOf(row), row.description ?? ''].some((text) => text.toLowerCase().includes(needle))
  })
  const grouped = GROUPS.map((group) => ({
    group,
    rows: visible.filter((row) => groupOf(row.key) === group).sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key)),
  })).filter((entry) => entry.rows.length > 0)

  const changedCount = rows.filter((row) => !row.isDefault).length
  const dirtyCount = rows.filter(isDirty).length

  // The index follows the scroll: the last group whose top has passed under the top bar is current.
  const groupIds = grouped.map((entry) => entry.group.id).join(',')
  useEffect(() => {
    const ids = groupIds === '' ? [] : groupIds.split(',')
    if (ids.length === 0) return
    const onScroll = (): void => {
      let current = ids[0]
      for (const id of ids) {
        const element = document.getElementById(groupId(id))
        if (element && element.getBoundingClientRect().top <= Math.max(140, window.innerHeight * 0.4)) current = id
      }
      // At the very bottom the last group is current even when it is short.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) current = ids[ids.length - 1]
      setActive(current)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [groupIds])

  const jump = (id: string): void => {
    setActive(id)
    document.getElementById(groupId(id))?.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }

  let jsonError: string | null = null
  if (jsonEditing) {
    try {
      parseValue(jsonText, jsonEditing.value, jsonEditing.key)
    } catch (error) {
      jsonError = error instanceof Error ? error.message : 'Значение не подходит'
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Настройки"
        description="Значения, которые платформа читает на лету. Комиссии — в «Сети и активы»."
        meta={
          list.data ? (
            <>
              <Badge tone="neutral" dot={false}>
                <span className="num">{rows.length}</span>&nbsp;{plural(rows.length, 'ключ', 'ключа', 'ключей')}
              </Badge>
              <Badge tone={changedCount > 0 ? 'accent' : 'neutral'}>
                <span className="num">{changedCount}</span>&nbsp;изменено оператором
              </Badge>
              <span className={styles.audited}>
                <ShieldCheck aria-hidden="true" />
                Каждое сохранение — с кодом 2FA и записью в аудит
              </span>
            </>
          ) : null
        }
      />

      <div className={styles.toolbar}>
        <Input
          leading={<Search />}
          placeholder="Найти: ключ, название, описание"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setQuery('')
          }}
          aria-label="Поиск по настройкам"
          className={styles.search}
          trailing={query !== '' ? <IconButton icon={<X />} label="Очистить поиск" size="sm" tooltip={false} onClick={() => setQuery('')} className={styles.clear} /> : undefined}
        />
        <SegmentedControl<Filter>
          value={filter}
          onChange={setFilter}
          ariaLabel="Какие настройки показать"
          options={[
            { value: 'all', label: 'Все', count: rows.length },
            { value: 'changed', label: 'Изменённые', count: changedCount },
            { value: 'default', label: 'По умолчанию', count: rows.length - changedCount },
          ]}
        />
        {dirtyCount > 0 ? (
          <Badge tone="warning" className={styles.dirtyBadge}>
            Не сохранено: {dirtyCount}
          </Badge>
        ) : null}
      </div>

      {list.isError ? (
        <Card>
          <EmptyState tone="error" icon={<SlidersHorizontal />} title="Настройки не загрузились" description={describeError(list.error)} action={<Button onClick={() => void list.refetch()}>Повторить</Button>} />
        </Card>
      ) : list.isPending ? (
        <div className={styles.layout} aria-busy="true">
          <nav className={styles.index} aria-hidden="true">
            <div className={styles.indexSkeleton}>
              {GROUPS.slice(0, 7).map((group) => (
                <Skeleton key={group.id} height={14} width="72%" />
              ))}
            </div>
          </nav>
          <div className={styles.groups}>
            {[0, 1, 2].map((index) => (
              <Card key={index}>
                <Skeleton height={16} width={180} />
                <div className={styles.skeletonRows}>
                  {[0, 1, 2].map((row) => (
                    <div key={row} className={styles.skeletonRow}>
                      <SkeletonText lines={3} />
                      <Skeleton height={36} radius={8} />
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      ) : grouped.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Search />}
            title={rows.length === 0 ? 'Настроек нет' : 'Ничего не нашлось'}
            description={rows.length === 0 ? 'Сервер не вернул ни одного ключа.' : 'Попробуйте другое слово или покажите все настройки.'}
            action={
              rows.length === 0 ? undefined : (
                <Button
                  onClick={() => {
                    setQuery('')
                    setFilter('all')
                  }}
                >
                  Сбросить фильтр
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className={styles.layout}>
          <nav className={styles.index} aria-label="Разделы настроек">
            <ul>
              {grouped.map(({ group, rows: groupRows }) => {
                const Icon = group.icon
                const dirty = groupRows.filter(isDirty).length
                return (
                  <li key={group.id}>
                    <button type="button" className={styles.indexItem} aria-current={active === group.id ? 'true' : undefined} onClick={() => jump(group.id)}>
                      <Icon aria-hidden="true" />
                      <span className={styles.indexLabel}>{group.title}</span>
                      {dirty > 0 ? <span className={styles.indexDirty} title="Есть несохранённые изменения" /> : null}
                      <span className={styles.indexCount}>{groupRows.length}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className={cx(styles.groups, 'stagger')}>
            {grouped.map(({ group, rows: groupRows }) => {
              const Icon = group.icon
              return (
                <section key={group.id} id={groupId(group.id)} className={styles.group} aria-label={group.title}>
                  <Card
                    flush
                    icon={<Icon />}
                    title={group.title}
                    description={group.description}
                    actions={
                      group.id === 'copy' ? (
                        <Badge tone="neutral">раздел выключен</Badge>
                      ) : (
                        <span className={styles.groupCount}>
                          {groupRows.length} {plural(groupRows.length, 'ключ', 'ключа', 'ключей')}
                        </span>
                      )
                    }
                  >
                    <div className={styles.rows}>
                      {groupRows.map((row) => (
                        <SettingRow
                          key={row.key}
                          row={row}
                          draft={draftOf(row)}
                          dirty={isDirty(row)}
                          error={errors[row.key]}
                          saving={save.isPending && save.variables?.key === row.key}
                          justSaved={saved === row.key}
                          flash={flash === row.key}
                          onDraft={(raw) => setDraft(row, raw)}
                          onReset={() => reset(row)}
                          onSave={() => commit(row, draftOf(row))}
                          onEditJson={() => openJson(row)}
                        />
                      ))}
                    </div>
                  </Card>
                </section>
              )
            })}
          </div>
        </div>
      )}

      <Modal
        open={jsonEditing !== null}
        onClose={() => setJsonEditing(null)}
        size="lg"
        icon={<Braces />}
        title={jsonEditing ? titleOf(jsonEditing) : ''}
        subtitle={jsonEditing?.description ?? undefined}
        footer={
          <>
            <span className={styles.modalNote}>Изменение попадёт в журнал аудита со старым и новым значением.</span>
            <Button variant="ghost" onClick={() => setJsonEditing(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              disabled={jsonError !== null || (jsonEditing !== null && sameValue(jsonEditing, jsonText))}
              loading={save.isPending && save.variables?.key === jsonEditing?.key}
              onClick={() => {
                if (!jsonEditing || jsonError !== null) return
                void withTotp((code) => save.mutateAsync({ key: jsonEditing.key, code, current: jsonEditing.value, raw: jsonText }))
              }}
            >
              Сохранить
            </Button>
          </>
        }
      >
        {jsonEditing ? (
          <div className={styles.jsonEditor}>
            <div className={styles.jsonKey}>
              <code>{jsonEditing.key}</code>
              {jsonEditing.isDefault ? (
                <Badge size="sm" dot={false}>
                  по умолчанию
                </Badge>
              ) : null}
            </div>
            <Field label="Значение, JSON" hint={valueHint(jsonEditing.value)} error={jsonError}>
              <Textarea mono spellCheck={false} rows={Math.min(18, Math.max(8, jsonText.split('\n').length + 1))} value={jsonText} onChange={(event) => setJsonText(event.target.value)} />
            </Field>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* one setting                                                              */
/* ------------------------------------------------------------------------ */

interface SettingRowProps {
  row: Setting
  draft: string
  dirty: boolean
  error: string | undefined
  saving: boolean
  justSaved: boolean
  flash: boolean
  onDraft: (raw: string) => void
  onReset: () => void
  onSave: () => void
  onEditJson: () => void
}

/** Title, key and description on the left; the typed control on the right, with its own save and an inline «Сохранено». */
function SettingRow({ row, draft, dirty, error, saving, justSaved, flash, onDraft, onReset, onSave, onEditJson }: SettingRowProps): JSX.Element {
  const control = controlOf(row)
  const meta = KEYS[row.key]
  const keys = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && dirty) {
      event.preventDefault()
      onSave()
    }
    if (event.key === 'Escape' && dirty) {
      event.preventDefault()
      onReset()
    }
  }

  let field: ReactNode
  if (control === 'bool') {
    const on = draft.trim() === 'true'
    field = (
      <div className={styles.switchLine}>
        <Switch checked={on} label={titleOf(row)} onChange={(next) => onDraft(String(next))} />
        <span className={cx(styles.switchState, on ? styles.switchOn : '')}>{on ? 'Включено' : 'Выключено'}</span>
      </div>
    )
  } else if (control === 'kinds') {
    const kinds = kindsOf(draft) ?? []
    field = (
      <div className={styles.kinds} role="group" aria-label={titleOf(row)}>
        {CHANNEL_ACCESS_KINDS.map((kind) => (
          <Chip
            key={kind}
            pressed={kinds.includes(kind)}
            onClick={() => onDraft(JSON.stringify(kinds.includes(kind) ? kinds.filter((one) => one !== kind) : CHANNEL_ACCESS_KINDS.filter((one) => one === kind || kinds.includes(one))))}
          >
            {ACCESS_RU[kind]}
          </Chip>
        ))}
      </div>
    )
  } else if (control === 'json') {
    field = (
      <div className={styles.jsonField}>
        <JsonPreview value={row.value} />
        <Button size="sm" icon={<Braces />} onClick={onEditJson}>
          Изменить JSON
        </Button>
      </div>
    )
  } else {
    const technical = control === 'number' || /channel|timezone|market|email|url|telegram/.test(row.key)
    field = (
      <Input
        mono={technical}
        inputMode={control === 'number' ? 'decimal' : undefined}
        value={draft}
        placeholder={meta?.placeholder ?? (control === 'text' ? 'пусто' : undefined)}
        trailing={meta?.unit ? <span className={styles.unit}>{meta.unit}</span> : undefined}
        invalid={error !== undefined}
        aria-label={titleOf(row)}
        onChange={(event) => onDraft(event.target.value)}
        onKeyDown={keys}
        className={control === 'number' ? styles.numberInput : styles.textInput}
        spellCheck={false}
      />
    )
  }

  return (
    <div id={rowId(row.key)} className={cx(styles.row, dirty ? styles.rowDirty : '', flash ? styles.rowFlash : '')}>
      <div className={styles.info}>
        <div className={styles.titleLine}>
          <span className={styles.title}>{titleOf(row)}</span>
          {row.isDefault ? (
            <Badge size="sm" dot={false} title="Значение не задавали — действует встроенное">
              по умолчанию
            </Badge>
          ) : null}
        </div>
        <code className={styles.key}>{row.key}</code>
        {row.description ? <p className={styles.description}>{row.description}</p> : null}
        {!row.isDefault && row.updatedAt ? <span className={styles.changed}>Изменено {when(row.updatedAt)}</span> : null}
      </div>
      <div className={styles.control}>
        {field}
        {error ? (
          <span className={styles.error} role="alert">
            {error}
          </span>
        ) : null}
        {dirty ? (
          <div className={styles.actions}>
            <Button size="sm" variant="ghost" onClick={onReset} disabled={saving}>
              Отменить
            </Button>
            <Button size="sm" variant="primary" icon={<Check />} loading={saving} onClick={onSave}>
              Сохранить
            </Button>
          </div>
        ) : justSaved ? (
          <span className={styles.saved} role="status">
            <Check aria-hidden="true" />
            Сохранено
          </span>
        ) : null}
      </div>
    </div>
  )
}

/** A list of flat objects as a small table (the leverage tiers); anything else as one line of JSON. */
function JsonPreview({ value }: { value: unknown }): JSX.Element {
  if (Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'object' && item !== null && !Array.isArray(item))) {
    const items = value as Array<Record<string, unknown>>
    const columns = Array.from(new Set(items.flatMap((item) => Object.keys(item))))
    return (
      <div className={styles.jsonTable}>
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 6).map((item, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column}>{editValue(item[column])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {items.length > 6 ? <div className={styles.jsonMore}>ещё {items.length - 6}</div> : null}
      </div>
    )
  }
  return <code className={styles.jsonLine}>{JSON.stringify(value)}</code>
}

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record
  const next = { ...record }
  delete next[key]
  return next
}

function rank(key: string): number {
  const index = KEY_ORDER.indexOf(key)
  return index === -1 ? KEY_ORDER.length : index
}

function rowId(key: string): string {
  return `setting-${key.replace(/[^a-z0-9_-]/gi, '-')}`
}

function groupId(id: string): string {
  return `settings-group-${id}`
}

function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}
